import React, { Suspense, useEffect, useMemo, useState } from 'react'
import { gql, useClient, useMutation, useQuery } from 'urql'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { useURLParam } from '../actions'
import AppLink from '../util/AppLink'
import CreateAlertDialog from './CreateAlertDialog'
import { ackLabel, ago, useAcks } from './api'
import { useServiceKinds } from './reports'
import {
  ChipTabs,
  Empty,
  LoadMore,
  Page,
  Panel,
  SearchBox,
  TimeFilter,
  useBrand,
  useNow,
  useTimeFilter,
} from './ui'

// GoAlert's urql client suspends by default; these pages render their own loading states.
const noSuspense = { suspense: false }

const listQuery = gql`
  query ErecordsAlertsPage($input: AlertSearchOptions) {
    alerts(input: $input) {
      nodes {
        id
        alertID
        status
        summary
        createdAt
        service {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

// open alerts per service (for the counts in the tabs and the service filter)
// idsQuery fetches just the numbers of every alert matching the list's filters,
// for "Select all" (the list itself only loads a page at a time).
const idsQuery = gql`
  query ErecordsAlertIDs($input: AlertSearchOptions!) {
    alerts(input: $input) {
      nodes {
        id
        alertID
        status
      }
      pageInfo {
        hasNextPage
      }
    }
  }
`
const SELECT_ALL_MAX = 1000

const countQuery = gql`
  query ErecordsAlertCounts($input: AlertSearchOptions) {
    alerts(input: $input) {
      nodes {
        id
        status
        serviceID
      }
      pageInfo {
        hasNextPage
      }
    }
    services(input: { first: 100 }) {
      nodes {
        id
        name
      }
    }
  }
`

const scopeQuery = gql`
  query ErecordsAlertScope($policyID: ID!) {
    escalationPolicy(id: $policyID) {
      id
      name
      assignedTo {
        id
        type
      }
    }
  }
`

const updateMutation = gql`
  mutation ErecordsUpdateAlerts($input: UpdateAlertsInput!) {
    updateAlerts(input: $input) {
      id
    }
  }
`
const escalateMutation = gql`
  mutation ErecordsEscalateAlerts($input: [Int!]) {
    escalateAlerts(input: $input) {
      id
    }
  }
`

type Status = 'StatusUnacknowledged' | 'StatusAcknowledged' | 'StatusClosed'
type Tab = 'unacked' | 'acked' | 'closed' | 'all'
type Severity = 'any' | 'critical' | 'warning'
type AlertNode = {
  id: string
  alertID: number
  status: Status
  summary: string
  createdAt: string
  service: { id: string; name: string } | null
}

const statusesFor: Record<Tab, Status[]> = {
  unacked: ['StatusUnacknowledged'],
  acked: ['StatusAcknowledged'],
  closed: ['StatusClosed'],
  all: [],
}

export function severityOf(name = ''): 'critical' | 'warning' | '' {
  const n = name.toLowerCase()
  if (n.includes('critical')) return 'critical'
  if (n.includes('warning')) return 'warning'
  return ''
}

export function SeverityChip({
  name,
}: {
  name?: string
}): React.JSX.Element | null {
  const sev = severityOf(name)
  if (!sev) return null
  return (
    <Chip
      size='small'
      variant='outlined'
      color={sev === 'critical' ? 'error' : 'warning'}
      label={sev === 'critical' ? 'Critical' : 'Warning'}
      sx={{ height: 20, fontSize: 11 }}
    />
  )
}

export function StatusChip({ status }: { status: Status }): React.JSX.Element {
  switch (status) {
    case 'StatusUnacknowledged':
      return (
        <Chip
          size='small'
          color='error'
          variant='outlined'
          label='Unacknowledged'
        />
      )
    case 'StatusAcknowledged':
      return (
        <Chip
          size='small'
          color='warning'
          variant='outlined'
          label='Acknowledged'
        />
      )
    default:
      return (
        <Chip size='small' color='success' variant='outlined' label='Closed' />
      )
  }
}

function AlertChunk(props: {
  input: Record<string, unknown>
  now: number
  selected: Set<number>
  onSelect: (s: Set<number>) => void
  showStatus: boolean
  emptyText: string
  cursor: string
  last: boolean
  onMore: (cursor: string) => void
}): React.JSX.Element {
  const theme = useTheme()
  const c = useBrand()
  const [q] = useQuery({
    query: listQuery,
    variables: { input: { ...props.input, first: 50, after: props.cursor } },
    requestPolicy: 'cache-and-network',
    context: noSuspense,
  })
  const nodes: AlertNode[] = q.data?.alerts?.nodes ?? []
  const info = q.data?.alerts?.pageInfo
  const acks = useAcks(
    nodes
      .filter((a) => a.status !== 'StatusUnacknowledged')
      .map((a) => a.alertID),
  )

  if (q.error) return <MUIAlert severity='error'>{q.error.message}</MUIAlert>
  if (!q.data)
    return <Typography sx={{ p: 2.5, color: c.muted }}>Loading…</Typography>
  if (!nodes.length && !props.cursor)
    return <Empty title='No alerts' text={props.emptyText} />

  const dot = (name?: string): string => {
    const s = severityOf(name)
    return s === 'critical'
      ? theme.palette.error.main
      : s === 'warning'
        ? c.warn
        : c.line
  }
  const toggle = (id: number): void => {
    const s = new Set(props.selected)
    if (s.has(id)) s.delete(id)
    else s.add(id)
    props.onSelect(s)
  }

  return (
    <React.Fragment>
      {nodes.map((a) => (
        <Box
          key={a.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pl: 1.5,
            pr: 2,
            minHeight: 48,
            '& + &': { borderTop: `1px solid ${c.line}` },
            '&:hover': { bgcolor: c.hover },
          }}
        >
          {a.status !== 'StatusClosed' ? (
            <Checkbox
              size='small'
              checked={props.selected.has(a.alertID)}
              onChange={() => toggle(a.alertID)}
              inputProps={{ 'aria-label': `Select alert ${a.alertID}` }}
            />
          ) : (
            <Box sx={{ width: 38 }} />
          )}
          <Box
            aria-hidden
            title={severityOf(a.service?.name) || 'no severity'}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: dot(a.service?.name),
              flexShrink: 0,
            }}
          />
          <AppLink
            to={`/alerts/${a.alertID}`}
            underline='none'
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'baseline',
              gap: 1,
              color: c.ink,
              py: 1.25,
            }}
          >
            <Typography
              component='span'
              sx={{ fontSize: 12, color: c.muted, flexShrink: 0 }}
            >
              #{a.alertID}
            </Typography>
            <Typography
              component='span'
              sx={{ fontSize: 14, fontWeight: 500 }}
              noWrap
            >
              {a.summary}
            </Typography>
            {acks[a.alertID] && (
              <Typography
                component='span'
                title={new Date(acks[a.alertID].at).toLocaleString()}
                sx={{
                  fontSize: 12,
                  color: c.muted,
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'inline' },
                }}
              >
                Acked by {ackLabel(acks[a.alertID], props.now)}
              </Typography>
            )}
          </AppLink>
          {props.showStatus && <StatusChip status={a.status} />}
          <Typography
            sx={{
              fontSize: 12,
              color: c.muted,
              width: 180,
              textAlign: 'right',
              display: { xs: 'none', md: 'block' },
            }}
            noWrap
          >
            {a.service?.name}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: c.muted,
              width: 64,
              textAlign: 'right',
              flexShrink: 0,
            }}
            title={new Date(a.createdAt).toLocaleString()}
          >
            {ago(a.createdAt, props.now)}
          </Typography>
        </Box>
      ))}
      <LoadMore
        show={props.last && Boolean(info?.hasNextPage)}
        loading={q.fetching}
        onClick={() => props.onMore(info.endCursor)}
      />
    </React.Fragment>
  )
}

// AlertList pages through alerts with "Show more"; one compact line per alert.
function AlertList(props: {
  input: Record<string, unknown>
  now: number
  selected: Set<number>
  onSelect: (s: Set<number>) => void
  showStatus: boolean
  emptyText: string
}): React.JSX.Element {
  const [cursors, setCursors] = useState<string[]>([''])
  return (
    <React.Fragment>
      {cursors.map((cursor, i) => (
        <AlertChunk
          key={cursor || 'first'}
          {...props}
          cursor={cursor}
          last={i === cursors.length - 1}
          onMore={(next) => setCursors([...cursors, next])}
        />
      ))}
    </React.Fragment>
  )
}

// AlertsPage is /alerts, and also /services/:serviceID/alerts and
// /escalation-policies/:policyID/alerts (scoped to that service or policy).
// Filters: status tabs, service (with open counts), severity, search.
export default function AlertsPage(props: {
  serviceID?: string
  policyID?: string
}): React.JSX.Element {
  const c = useBrand()
  const now = useNow()
  const [tab, setTab] = useURLParam<string>('status', 'unacked')
  const [serviceParam, setService] = useURLParam<string>('service', '')
  const [sev, setSev] = useURLParam<string>('severity', 'any')
  const [search, setSearch] = useURLParam<string>('search', '')
  const [creating, setCreating] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const [sq] = useQuery({
    query: scopeQuery,
    variables: { policyID: props.policyID ?? '' },
    pause: !props.policyID,
    context: noSuspense,
  })
  const tf = useTimeFilter()
  const [counts] = useQuery({
    query: countQuery,
    variables: {
      input: {
        search,
        ...tf.range,
        filterByStatus: ['StatusUnacknowledged', 'StatusAcknowledged'],
        first: 1000,
      },
    },
    requestPolicy: 'cache-and-network',
    context: noSuspense,
  })

  const services: { id: string; name: string }[] =
    counts.data?.services?.nodes ?? []
  const policyServices: string[] = (sq.data?.escalationPolicy?.assignedTo ?? [])
    .filter((t: { type: string }) => t.type === 'service')
    .map((t: { id: string }) => t.id)
  const service = props.serviceID ?? serviceParam
  const scopeIDs: string[] | undefined = props.serviceID
    ? [props.serviceID]
    : props.policyID
      ? policyServices
      : undefined

  // which services the list shows: picked service > severity > page scope
  // closed/all tabs leave out report services (their items live on /reports;
  // open report failures still show in the open tabs)
  const kinds = useServiceKinds()
  const reportIDs = kinds.services.filter((k) => k.report).map((k) => k.id)
  const hideReports =
    !service &&
    !props.serviceID &&
    (tab === 'closed' || tab === 'all') &&
    reportIDs.length > 0
  const ids = useMemo(() => {
    let list = scopeIDs ?? services.map((s) => s.id)
    if (service) list = [service]
    if (hideReports) list = list.filter((id) => !reportIDs.includes(id))
    if (sev !== 'any') {
      list = list.filter(
        (id) => severityOf(services.find((s) => s.id === id)?.name) === sev,
      )
    }
    return list
  }, [
    JSON.stringify(scopeIDs),
    service,
    sev,
    services.length,
    hideReports,
    reportIDs.join(),
  ])
  const filtered = Boolean(scopeIDs || service || sev !== 'any' || hideReports)
  const base = {
    search,
    ...tf.range,
    ...(filtered ? { filterByServiceID: ids.length ? ids : ['none'] } : {}),
  }
  useEffect(
    () => setSelected(new Set()),
    [tab, service, sev, search, JSON.stringify(tf.range)],
  )

  const open: { status: Status; serviceID: string }[] =
    counts.data?.alerts?.nodes ?? []
  const per = new Map<string, { unack: number; ack: number }>()
  for (const a of open) {
    const n = per.get(a.serviceID) ?? { unack: 0, ack: 0 }
    if (a.status === 'StatusUnacknowledged') n.unack++
    else n.ack++
    per.set(a.serviceID, n)
  }
  const inScope = open.filter((a) => !filtered || ids.includes(a.serviceID))
  const nUnack = inScope.filter(
    (a) => a.status === 'StatusUnacknowledged',
  ).length
  const nAck = inScope.length - nUnack
  const capped = counts.data?.alerts?.pageInfo?.hasNextPage

  const [upd, commitUpdate] = useMutation(updateMutation)
  const [esc, commitEscalate] = useMutation(escalateMutation)
  const busy = upd.fetching || esc.fetching
  const actionError = upd.error || esc.error
  const picked = Array.from(selected)
  const clear = (): void => setSelected(new Set())
  const client = useClient()
  const [selecting, setSelecting] = useState(false)
  const [selectCapped, setSelectCapped] = useState(false)
  const listInput = { ...base, filterByStatus: statusesFor[tab as Tab] ?? [] }
  const selectAll = (): void => {
    if (selected.size) {
      clear()
      return
    }
    setSelecting(true)
    client
      .query(
        idsQuery,
        { input: { ...listInput, first: SELECT_ALL_MAX } },
        { requestPolicy: 'network-only' },
      )
      .toPromise()
      .then((r) => {
        const nodes: { alertID: number; status: string }[] =
          r.data?.alerts?.nodes ?? []
        setSelected(
          new Set(
            nodes
              .filter((a) => a.status !== 'StatusClosed')
              .map((a) => a.alertID),
          ),
        )
        setSelectCapped(Boolean(r.data?.alerts?.pageInfo?.hasNextPage))
      })
      .finally(() => setSelecting(false))
  }
  const update = (newStatus: Status): void => {
    commitUpdate(
      { input: { alertIDs: picked, newStatus } },
      { additionalTypenames: ['Alert'] },
    ).then(clear)
  }

  const scopeName =
    (props.serviceID && services.find((s) => s.id === props.serviceID)?.name) ||
    sq.data?.escalationPolicy?.name ||
    ''
  const pickable = (scopeIDs ?? services.map((s) => s.id))
    .map((id) => services.find((s) => s.id === id))
    .filter(Boolean) as { id: string; name: string }[]
  const label = (id: string, name: string): string => {
    const n = per.get(id)
    const total = (n?.unack ?? 0) + (n?.ack ?? 0)
    return total ? `${name} · ${total} open` : name
  }

  return (
    <Page
      title={scopeName ? `Alerts · ${scopeName}` : 'Alerts'}
      subtitle='Unacknowledged alerts keep escalating (and can call people) until someone acknowledges them.'
      actions={
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setCreating(true)}
        >
          Create alert
        </Button>
      }
    >
      <Suspense>
        {creating && (
          <CreateAlertDialog
            serviceID={props.serviceID}
            onlyIDs={props.policyID ? policyServices : undefined}
            onClose={() => setCreating(false)}
          />
        )}
      </Suspense>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <ChipTabs<Tab>
          value={tab as Tab}
          onChange={setTab}
          options={[
            {
              value: 'unacked',
              label: 'Unacknowledged',
              count: counts.data ? nUnack : undefined,
            },
            {
              value: 'acked',
              label: 'Acknowledged',
              count: counts.data ? nAck : undefined,
            },
            { value: 'closed', label: 'Closed' },
            { value: 'all', label: 'All' },
          ]}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          alignItems: 'center',
          mb: 2,
        }}
      >
        {!props.serviceID && (
          <TextField
            select
            size='small'
            label='Service'
            value={service}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setService(e.target.value)}
            sx={{ minWidth: 260 }}
          >
            <MenuItem value=''>
              All services
              {counts.data ? ` · ${open.length}${capped ? '+' : ''} open` : ''}
            </MenuItem>
            <ListSubheader>Services</ListSubheader>
            {pickable.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {label(s.id, s.name)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <ChipTabs<Severity>
          value={sev as Severity}
          onChange={setSev}
          options={[
            { value: 'any', label: 'Any severity' },
            { value: 'critical', label: 'Critical' },
            { value: 'warning', label: 'Warning' },
          ]}
        />
        <TimeFilter f={tf} />
        <Box sx={{ flex: 1 }} />
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder='Search summary or #id'
        />
      </Box>

      {selected.size > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: '12px',
            bgcolor: c.soft,
            border: `1px solid ${c.line}`,
          }}
        >
          <Typography sx={{ fontWeight: 600, mr: 1 }}>
            {selected.size} selected
            {selectCapped && selected.size >= SELECT_ALL_MAX && (
              <Box
                component='span'
                sx={{ fontWeight: 400, color: c.muted, fontSize: 13 }}
              >
                {' '}
                (the newest {SELECT_ALL_MAX})
              </Box>
            )}
          </Typography>
          <Button
            size='small'
            variant='contained'
            disabled={busy}
            onClick={() => update('StatusAcknowledged')}
          >
            Acknowledge
          </Button>
          <Button
            size='small'
            variant='outlined'
            disabled={busy}
            onClick={() => update('StatusClosed')}
          >
            Close
          </Button>
          <Button
            size='small'
            variant='outlined'
            disabled={busy}
            onClick={() =>
              commitEscalate(
                { input: picked },
                { additionalTypenames: ['Alert'] },
              ).then(clear)
            }
          >
            Escalate
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size='small' onClick={clear}>
            Clear
          </Button>
        </Box>
      )}
      {actionError && (
        <MUIAlert severity='error' sx={{ mb: 1.5 }}>
          {actionError.message}
        </MUIAlert>
      )}

      <Panel flush>
        {tab !== 'closed' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 1.5,
              pr: 2,
              minHeight: 44,
              borderBottom: `1px solid ${c.line}`,
            }}
          >
            <Checkbox
              size='small'
              checked={selected.size > 0}
              indeterminate={selected.size > 0 && selecting}
              disabled={selecting}
              onChange={selectAll}
              inputProps={{
                'aria-label': selected.size
                  ? 'Clear selection'
                  : 'Select all matching alerts',
              }}
            />
            <Typography
              component='button'
              type='button'
              onClick={selectAll}
              disabled={selecting}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: c.ink,
              }}
            >
              {selecting
                ? 'Selecting…'
                : selected.size
                  ? `${selected.size} selected · clear`
                  : 'Select all'}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 12, color: c.muted }}>
              Newest first
            </Typography>
          </Box>
        )}
        <AlertList
          key={JSON.stringify([tab, base])}
          input={listInput}
          now={now}
          selected={selected}
          onSelect={setSelected}
          showStatus={tab === 'all'}
          emptyText={
            tab === 'unacked'
              ? 'Nothing is waiting for someone. All quiet.'
              : 'No alerts match these filters.'
          }
        />
      </Panel>
    </Page>
  )
}
